import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";
import { safeFormatDistanceToNow } from "@/utils/dateUtils";
import type { GeminiVerification } from "@/types/security";
import { useState } from "react";

interface VerificationCardProps {
  verification: GeminiVerification;
  verifiedAt?: string | null;
}

export function VerificationCard({ verification, verifiedAt }: VerificationCardProps) {
  const [expanded, setExpanded] = useState(false);

  const confidence = verification.confidence;
  const confidencePercent = Math.round(confidence * 100);
  const gaugeColor =
    confidence >= 0.8 ? "bg-green-500" : confidence >= 0.5 ? "bg-yellow-500" : "bg-red-500";
  const isConfirmed = verification.is_real_threat;

  return (
    <div className="pt-4 border-t">
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4" />
        AI Verification
      </h3>
      <div className="space-y-3">
        <div className="flex items-center gap-4">
          {isConfirmed ? (
            <Badge variant="destructive">Confirmed Threat</Badge>
          ) : (
            <Badge className="bg-green-100 text-green-700 border-green-300">
              Likely False Positive
            </Badge>
          )}
          {verifiedAt && (
            <span className="text-xs text-muted-foreground">
              Verified {safeFormatDistanceToNow(verifiedAt, { addSuffix: true })}
            </span>
          )}
        </div>

        {confidence != null ? (
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-muted-foreground">Confidence</span>
              <span className="font-medium">{confidencePercent}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`${gaugeColor} h-2 rounded-full transition-all`}
                style={{ width: `${confidencePercent}%` }}
              />
            </div>
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">Unknown confidence</div>
        )}

        {verification.reasoning && (
          <div>
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {expanded ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
              Reasoning
            </button>
            {expanded && (
              <div className="mt-2 p-3 bg-gray-50 rounded border border-gray-200 text-xs leading-relaxed">
                {verification.reasoning}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
